import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';

const CommunityAreaMap = dynamic(
  () => import('../../components/communities/CommunityAreaMap'),
  { ssr: false, loading: () => <div className="h-[360px] rounded-lg border border-[var(--c-border)] bg-[var(--c-surface)] animate-pulse" /> }
);
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft,
  Bell,
  Map,
  FileText,
  ChevronRight,
  Clock,
  AlertCircle,
  CheckCircle,
  XCircle,
  Layers,
  MessageCircle,
  TrendingUp,
  Home,
  Building2,
  Calendar,
  Activity,
  MapPin,
  Download,
  Star,
  AlertTriangle,
  MinusCircle,
  X,
  type LucideIcon,
} from 'lucide-react';
import Meta from '../../components/Meta';
import {
  type Application,
  type Community,
  type Decision,
  type MajorProject,
  type TimelineEvent,
  type ScaleRating,
  type ApplicationStatus,
} from '../../lib/communities/types';
import { communityAreas } from '../../lib/communities/areaDefinitions';
import type { AreaPageData, CommunityWeeklyReportData, MapPoint, TrendDirection } from '../../lib/communities/dataService';
import { buildPlanningApplicationPath } from '../../lib/planningApplicationUrl';
import { subscribeToEmailReport } from '../../lib/emailReports';
import { suppressWelcomeBackPrompt } from '../../lib/welcomeBackPromptSuppression';
import { parseRetryAfterMs, rateLimitRetryAt } from '../../lib/httpRetryAfter';

const CITY_SCOPE_API_ORIGIN = (() => {
  const configured = process.env.NEXT_PUBLIC_API_URL ?? 'https://api.city-scope.co.uk';
  try {
    return new URL(configured).origin;
  } catch {
    return 'https://api.city-scope.co.uk';
  }
})();

const ApplicationDetailDrawer = dynamic(
  () => import('../../components/communities/ApplicationDetailDrawer'),
  { ssr: false }
);

// ─── Helpers ──────────────────────────────────────────────────────────────────

type Tab = 'overview' | 'applications' | 'deadlines' | 'projects' | 'discussion';

const scaleColors: Record<ScaleRating, string> = {
  unknown: 'bg-[var(--c-text-tertiary)]/10 text-[var(--c-text-tertiary)] border-[var(--c-border)]',
  city: 'bg-[var(--c-error)]/12 text-[var(--c-error)] border-[var(--c-error)]/30',
  district: 'bg-orange-500/10 text-orange-400 border-orange-400/30',
  local: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30',
  minor: 'bg-[var(--c-accent)]/10 text-[var(--c-accent)] border-[var(--c-accent)]/30',
};

const scaleLabels: Record<ScaleRating, string> = {
  unknown: 'Scale not available',
  city: 'City-shaping',
  district: 'District impact',
  local: 'Local change',
  minor: 'Minor scale',
};

const scaleDots: Record<ScaleRating, string> = {
  unknown: 'bg-[var(--c-text-tertiary)]',
  city: 'bg-[var(--c-error)]',
  district: 'bg-orange-400',
  local: 'bg-emerald-500',
  minor: 'bg-[var(--c-accent)]',
};

// ─── Sparkline ────────────────────────────────────────────────────────────────

function Sparkline({
  data,
  color = 'var(--c-accent)',
  height = 24,
  width = 60,
}: {
  data: number[];
  color?: string;
  height?: number;
  width?: number;
}) {
  if (!data || data.length < 2) return null;
  const max = Math.max(...data, 1);
  const step = width / (data.length - 1);
  const pts = data.map((v, i) => {
    const x = i * step;
    const y = height - (v / max) * (height - 2) - 1;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  const area = [
    `0,${height}`,
    ...pts,
    `${width},${height}`,
  ].join(' ');

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className="overflow-visible opacity-70"
      aria-hidden
    >
      <polygon
        points={area}
        fill={color}
        fillOpacity={0.12}
      />
      <polyline
        points={pts.join(' ')}
        fill="none"
        stroke={color}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* Last point dot */}
      {pts.length > 0 && (() => {
        const last = pts[pts.length - 1].split(',');
        return (
          <circle
            cx={last[0]}
            cy={last[1]}
            r={2}
            fill={color}
          />
        );
      })()}
    </svg>
  );
}

// ─── Trend helpers ────────────────────────────────────────────────────────────

function TrendBadge({ direction }: { direction: TrendDirection }) {
  if (direction === 'stable') return null;
  const isUp = direction === 'up';
  return (
    <span
      className={`ml-1 inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 font-['DM_Mono'] text-[9px] ${
        isUp
          ? 'bg-emerald-500/10 text-emerald-400'
          : 'bg-rose-500/10 text-rose-400'
      }`}
      title={isUp ? 'Higher than last week' : 'Lower than last week'}
    >
      {isUp ? '▲' : '▼'}
    </span>
  );
}

function formatDate(value: string | null | undefined): string {
  if (!value) return 'Information not available';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Information not available';
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) return 'Information not available';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Information not available';
  return date.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const statusColorMap: Record<Community['statusColor'], string> = {
  amber: 'bg-[var(--c-accent)]/10 text-[var(--c-accent)] border-[var(--c-accent)]/25',
  red: 'bg-[var(--c-error)]/10 text-[var(--c-error)] border-[var(--c-error)]/25',
  blue: 'bg-blue-500/10 text-blue-400 border-blue-400/25',
  green: 'bg-emerald-500/10 text-emerald-400 border-emerald-400/25',
  purple: 'bg-purple-500/10 text-purple-400 border-purple-400/25',
  gray: 'bg-[var(--c-text-tertiary)]/10 text-[var(--c-text-tertiary)] border-[var(--c-text-tertiary)]/20',
};

const statusStyles: Record<ApplicationStatus, { badge: string; label: string; icon: LucideIcon }> = {
  unknown: { badge: 'bg-[var(--c-text-tertiary)]/10 text-[var(--c-text-tertiary)] border-[var(--c-border)]', label: 'Status not available', icon: AlertCircle },
  pending: {
    badge: 'bg-[var(--c-accent)]/10 text-[var(--c-accent)] border-[var(--c-accent)]/30',
    label: 'Pending',
    icon: Clock,
  },
  approved: {
    badge: 'bg-emerald-500/10 text-emerald-400 border-emerald-400/30',
    label: 'Approved',
    icon: CheckCircle,
  },
  refused: {
    badge: 'bg-[var(--c-error)]/10 text-[var(--c-error)] border-[var(--c-error)]/30',
    label: 'Refused',
    icon: XCircle,
  },
  withdrawn: {
    badge: 'bg-[var(--c-text-tertiary)]/10 text-[var(--c-text-tertiary)] border-[var(--c-text-tertiary)]/20',
    label: 'Withdrawn',
    icon: MinusCircle,
  },
  appeal: {
    badge: 'bg-purple-500/10 text-purple-400 border-purple-400/30',
    label: 'Appeal',
    icon: AlertTriangle,
  },
};

const decisionStyles: Record<Decision['decision'], { cls: string; label: string; icon: LucideIcon }> = {
  approved: { cls: 'text-emerald-400', label: 'Approved', icon: CheckCircle },
  refused: { cls: 'text-[var(--c-error)]', label: 'Refused', icon: XCircle },
  withdrawn: { cls: 'text-[var(--c-text-tertiary)]', label: 'Withdrawn', icon: MinusCircle },
  'appeal-allowed': { cls: 'text-purple-400', label: 'Appeal allowed', icon: AlertTriangle },
  'appeal-dismissed': { cls: 'text-orange-400', label: 'Appeal dismissed', icon: AlertTriangle },
};

const timelineTypeConfig: Record<TimelineEvent['type'], { icon: LucideIcon; color: string; dotColor: string }> = {
  'new-application': { icon: FileText, color: 'text-[var(--c-accent)]', dotColor: 'bg-[var(--c-accent)]' },
  'documents-added': { icon: Download, color: 'text-blue-400', dotColor: 'bg-blue-400' },
  'decision-issued': { icon: CheckCircle, color: 'text-emerald-400', dotColor: 'bg-emerald-400' },
  'related-detected': { icon: Layers, color: 'text-purple-400', dotColor: 'bg-purple-400' },
  'comment-received': { icon: MessageCircle, color: 'text-[var(--c-info)]', dotColor: 'bg-[var(--c-info)]' },
};

const FADE_UP = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: "easeOut" as const } },
};

const FADE_IN = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { duration: 0.35 } },
};

// ─── Sub-components ───────────────────────────────────────────────────────────

function SnapshotCard({
  icon: Icon,
  label,
  value,
  sub,
  highlight,
  trend,
  sparkData,
}: {
  icon: LucideIcon;
  label: string;
  value: number | string;
  sub?: string;
  highlight?: boolean;
  trend?: TrendDirection;
  sparkData?: number[];
}) {
  return (
    <div
      className={`flex flex-col gap-2 rounded-lg border p-4 transition-colors ${
        highlight
          ? 'border-[var(--c-accent)]/30 bg-[var(--c-accent)]/5'
          : 'border-[var(--c-border)] bg-[var(--c-surface)] hover:border-[var(--c-accent)]/20'
      }`}
    >
      <div className="flex items-center gap-2">
        <Icon
          className={`h-3.5 w-3.5 ${highlight ? 'text-[var(--c-accent)]' : 'text-[var(--c-text-tertiary)]'}`}
        />
        <span className="font-['DM_Mono'] text-[10px] uppercase tracking-[0.12em] text-[var(--c-text-tertiary)]">
          {label}
        </span>
      </div>
      <div className="flex items-end justify-between gap-2">
        <div className="flex items-end gap-2">
          <span
            className={`font-['Space_Grotesk'] text-2xl font-700 leading-none ${
              highlight ? 'text-[var(--c-accent)]' : 'text-[var(--c-text-primary)]'
            }`}
          >
            {typeof value === 'number' ? value.toLocaleString() : value}
          </span>
          {trend && <TrendBadge direction={trend} />}
        </div>
        {sparkData && sparkData.some((v) => v > 0) && (
          <Sparkline
            data={sparkData}
            color={highlight ? 'var(--c-accent)' : 'var(--c-text-tertiary)'}
          />
        )}
      </div>
      {sub && <span className="font-['DM_Mono'] text-[10px] text-[var(--c-text-tertiary)]">{sub}</span>}
    </div>
  );
}

function WhySurfacedBadges({ reasons }: { reasons: string[] }) {
  const colorMap: Record<string, string> = {
    'City-shaping': 'bg-[var(--c-error)]/10 text-[var(--c-error)] border-[var(--c-error)]/25',
    'Short-term let': 'bg-purple-500/10 text-purple-400 border-purple-400/25',
    'Listed building': 'bg-blue-500/10 text-blue-400 border-blue-400/25',
    'Multiple related applications': 'bg-emerald-500/10 text-emerald-400 border-emerald-400/25',
    'Major development': 'bg-[var(--c-error)]/10 text-[var(--c-error)] border-[var(--c-error)]/25',
    'Adjacent listed building': 'bg-blue-500/10 text-blue-400 border-blue-400/25',
    'Residential to commercial': 'bg-purple-500/10 text-purple-400 border-purple-400/25',
  };
  return (
    <div className="flex flex-wrap gap-1.5">
      {reasons.map((reason) => (
        <span
          key={reason}
          className={`rounded-full border px-2 py-0.5 font-['DM_Mono'] text-[10px] uppercase tracking-wide ${
            colorMap[reason] ?? 'bg-[var(--c-surface)] text-[var(--c-text-tertiary)] border-[var(--c-border)]'
          }`}
        >
          {reason}
        </span>
      ))}
    </div>
  );
}

function ApplicationCard({
  app,
  compact = false,
  onOpenDrawer,
}: {
  app: Application;
  compact?: boolean;
  onOpenDrawer?: (app: Application) => void;
}) {
  const statusStyle = statusStyles[app.status];
  const StatusIcon = statusStyle.icon;

  return (
    <div
      className="group relative overflow-hidden rounded-lg border border-[var(--c-border)] bg-[var(--c-surface)] p-5 transition-all hover:border-[var(--c-accent)]/30 hover:shadow-[0_0_20px_rgba(211,175,55,0.06)]"
      onClick={onOpenDrawer ? () => onOpenDrawer(app) : undefined}
      style={onOpenDrawer ? { cursor: 'pointer' } : undefined}
    >
      {/* Left accent bar */}
      <div
        className={`absolute left-0 top-0 h-full w-0.5 ${
          app.scaleRating === 'city'
            ? 'bg-[var(--c-error)]'
            : app.scaleRating === 'district'
            ? 'bg-orange-400'
            : app.scaleRating === 'local'
            ? 'bg-emerald-500'
            : 'bg-[var(--c-accent)]/40'
        }`}
      />

      <div className="pl-2">
        {/* Header row */}
        <div className="mb-2 flex flex-wrap items-start gap-2">
          <span className="font-['DM_Mono'] text-[10px] uppercase tracking-wider text-[var(--c-text-tertiary)]">
            {app.reference}
          </span>
          <span
            className={`flex items-center gap-1 rounded-full border px-2 py-0.5 font-['DM_Mono'] text-[10px] ${statusStyle.badge}`}
          >
            <StatusIcon className="h-2.5 w-2.5" />
            {app.status === 'unknown' && app.sourceStatus ? app.sourceStatus : statusStyle.label}
          </span>
          <span
            className={`rounded-full border px-2 py-0.5 font-['DM_Mono'] text-[10px] ${scaleColors[app.scaleRating]}`}
          >
            {scaleLabels[app.scaleRating]}
          </span>
          <span className="rounded-full border border-[var(--c-border)] px-2 py-0.5 font-['DM_Mono'] text-[10px] text-[var(--c-text-tertiary)]">
            {app.category}
          </span>
        </div>

        {/* Address */}
        <p className="mb-1 text-sm font-600 font-['Space_Grotesk'] text-[var(--c-text-primary)] leading-snug">
          {app.address}
        </p>

        {/* AI Summary */}
        <p className="mb-3 text-xs leading-relaxed text-[var(--c-text-secondary)]">
          {app.aiSummary}
        </p>

        {/* Why surfaced */}
        <div className="mb-3">
          <WhySurfacedBadges reasons={app.whySurfaced} />
        </div>

        {/* Tags */}
        {!compact && (
          <div className="mb-3 flex flex-wrap gap-1">
            {app.tags.map((tag) => (
              <span
                key={tag}
                className="rounded-full border border-[var(--c-border)] px-2 py-0.5 font-['DM_Mono'] text-[10px] text-[var(--c-text-tertiary)]"
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Footer */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[var(--c-border)] pt-3">
          <div className="flex flex-wrap gap-3 text-xs text-[var(--c-text-tertiary)]">
            <span className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              Received {formatDate(app.receivedDate)}
            </span>
            {app.discussionCount > 0 && (
              <span className="flex items-center gap-1">
                <MessageCircle className="h-3 w-3" />
                {app.discussionCount} {app.discussionCount === 1 ? 'comment' : 'comments'}
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <Link
              href={buildPlanningApplicationPath(app.reference)}
              onClick={(e) => e.stopPropagation()}
              className="btn btn--primary text-xs py-1.5 px-3 flex items-center gap-1"
            >
              View application <ChevronRight className="h-3 w-3" />
            </Link>
            {app.portalLink && (
              <a
                href={app.portalLink}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="btn text-xs py-1.5 px-2 flex items-center gap-1 text-[var(--c-text-tertiary)] hover:text-[var(--c-text-secondary)]"
                title="Edinburgh Council portal"
              >
                <ArrowLeft className="h-3 w-3 rotate-[135deg]" />
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function DecisionRow({ dec }: { dec: Decision }) {
  const style = decisionStyles[dec.decision];
  const Icon = style.icon;
  return (
    <div className="flex items-start gap-3 py-3 border-b border-[var(--c-border)] last:border-0">
      <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${style.cls}`} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2 mb-0.5">
          <span className="font-['DM_Mono'] text-[10px] uppercase tracking-wider text-[var(--c-text-tertiary)]">
            {dec.reference}
          </span>
          <span
            className={`rounded-full border px-2 py-0.5 font-['DM_Mono'] text-[10px] ${scaleColors[dec.scaleRating]}`}
          >
            {scaleLabels[dec.scaleRating]}
          </span>
        </div>
        <p className="text-sm text-[var(--c-text-primary)] font-medium">{dec.address}</p>
        <p className="text-xs text-[var(--c-text-secondary)] mt-0.5">{dec.description}</p>
      </div>
      <div className="shrink-0 text-right">
        <p className={`font-['DM_Mono'] text-[11px] font-600 ${style.cls}`}>{style.label}</p>
        <p className="font-['DM_Mono'] text-[10px] text-[var(--c-text-tertiary)] mt-0.5">{formatDate(dec.decisionDate)}</p>
      </div>
    </div>
  );
}

function ProjectCard({ project }: { project: MajorProject }) {
  return (
    <div className="group rounded-lg border border-[var(--c-border)] bg-[var(--c-surface)] p-5 transition-colors hover:border-[var(--c-accent)]/30">
      <div className="mb-3 flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-full bg-[var(--c-accent)]/10">
            <Layers className="h-3.5 w-3.5 text-[var(--c-accent)]" />
          </div>
          <span className="rounded-full border border-emerald-400/25 bg-emerald-500/10 px-2 py-0.5 font-['DM_Mono'] text-[10px] uppercase tracking-wider text-emerald-400">
            {project.applicationCount} applications
          </span>
        </div>
        <span className="font-['DM_Mono'] text-[10px] text-[var(--c-text-tertiary)]">
          Updated {formatDate(project.lastUpdated)}
        </span>
      </div>
      <h3 className="mb-2 font-['Space_Grotesk'] text-sm font-600 text-[var(--c-text-primary)] leading-snug">
        {project.title}
      </h3>
      <p className="mb-3 text-xs leading-relaxed text-[var(--c-text-secondary)]">{project.description}</p>
      <div className="mb-4 flex flex-wrap gap-1.5">
        {project.tags.map((tag) => (
          <span
            key={tag}
            className="rounded-full border border-[var(--c-border)] px-2 py-0.5 font-['DM_Mono'] text-[10px] text-[var(--c-text-tertiary)]"
          >
            {tag}
          </span>
        ))}
      </div>
      <div className="flex items-center justify-between border-t border-[var(--c-border)] pt-3">
        <span className="font-['DM_Mono'] text-[10px] text-[var(--c-text-tertiary)]">
          {project.area}
        </span>
      </div>
    </div>
  );
}

function TimelineEventItem({ event, isLast }: { event: TimelineEvent; isLast: boolean }) {
  const config = timelineTypeConfig[event.type];
  const Icon = config.icon;
  return (
    <div className="relative flex gap-4">
      {/* Vertical line */}
      {!isLast && (
        <div className="absolute left-3.5 top-8 bottom-0 w-px bg-[var(--c-border)]" />
      )}
      {/* Dot */}
      <div className="relative z-10 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-[var(--c-border)] bg-[var(--c-surface)]">
        <Icon className={`h-3 w-3 ${config.color}`} />
      </div>
      {/* Content */}
      <div className="flex-1 pb-5">
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <p className="text-sm font-medium text-[var(--c-text-primary)]">{event.title}</p>
          {event.timestamp && (
            <span className="font-['DM_Mono'] text-[10px] text-[var(--c-text-tertiary)]">
              {formatDate(event.timestamp)}
            </span>
          )}
        </div>
        <p className="text-xs leading-relaxed text-[var(--c-text-secondary)]">{event.description}</p>
        {event.reference && (
          <span className="mt-1.5 inline-block font-['DM_Mono'] text-[10px] uppercase tracking-wider text-[var(--c-accent)]">
            {event.reference}
          </span>
        )}
      </div>
    </div>
  );
}

function MapPlaceholder() {
  return (
    <div className="flex h-64 items-center justify-center rounded-lg border border-[var(--c-border)] bg-[var(--c-surface)] p-6 text-center">
      <div>
        <MapPin className="mx-auto mb-2 h-6 w-6 text-[var(--c-text-tertiary)]" />
        <p className="text-sm text-[var(--c-text-secondary)]">Map information not available.</p>
      </div>
    </div>
  );
}

function DeferredCommunityAreaMap({
  mapPoints,
  onSelectRef,
}: {
  mapPoints: MapPoint[];
  onSelectRef: (reference_no: string) => void;
}) {
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const [shouldLoadMap, setShouldLoadMap] = useState(false);

  useEffect(() => {
    if (shouldLoadMap) return undefined;
    const node = sentinelRef.current;
    if (!node || typeof window === 'undefined') return undefined;

    if (!('IntersectionObserver' in window)) {
      setShouldLoadMap(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setShouldLoadMap(true);
          observer.disconnect();
        }
      },
      { rootMargin: '700px 0px' }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [shouldLoadMap]);

  if (shouldLoadMap) {
    return <CommunityAreaMap mapPoints={mapPoints} onSelectRef={onSelectRef} />;
  }

  return (
    <div
      ref={sentinelRef}
      className="h-[380px] overflow-hidden rounded-lg border border-[var(--c-border)] bg-[var(--c-surface)]"
      aria-label="Application map"
    >
      <div className="h-full animate-pulse bg-[linear-gradient(135deg,rgba(211,175,55,0.08),rgba(255,255,255,0.02),rgba(211,175,55,0.05))]" />
    </div>
  );
}
// Follow area button (wired to email subscription API) ────────────────────

function FollowAreaButton({ slug, areaName }: { slug: string; areaName: string }) {
  const [state, setState] = useState<'idle' | 'prompt' | 'done' | 'error'>('idle');
  const [submitting, setSubmitting] = useState(false);
  const [email, setEmail] = useState('');

  if (state === 'done') {
    return (
      <span className="flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1.5 font-['DM_Mono'] text-[11px] text-emerald-400">
        <CheckCircle className="h-3 w-3" /> Following {areaName}
      </span>
    );
  }

  if (state === 'prompt') {
    return (
      <form
        className="flex items-center gap-2"
        onSubmit={async (e) => {
          e.preventDefault();
          if (!email.trim()) return;
          setSubmitting(true);
          try {
            const response = await subscribeToEmailReport(`community_weekly_${slug}`, {
              emailOverride: email.trim(),
              source: 'communities_follow',
            });
            if (response.ok) {
              suppressWelcomeBackPrompt('community_follow_success');
            }
            setState(response.ok ? 'done' : 'error');
          } catch {
            setState('error');
          } finally {
            setSubmitting(false);
          }
        }}
      >
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="your@email.com"
          autoFocus
          className="h-8 rounded-md border border-[var(--c-border)] bg-[var(--c-surface)] px-3 font-['DM_Mono'] text-[11px] text-[var(--c-text-primary)] placeholder-[var(--c-text-tertiary)] focus:border-[var(--c-accent)] focus:outline-none"
        />
        <button
          type="submit"
          disabled={submitting}
          className="btn btn--primary py-1 text-xs disabled:opacity-50"
        >
          {submitting ? '…' : 'Subscribe'}
        </button>
        <button
          type="button"
          onClick={() => setState('idle')}
          className="font-['DM_Mono'] text-[10px] text-[var(--c-text-tertiary)] hover:text-[var(--c-text-secondary)]"
        >
          Cancel
        </button>
      </form>
    );
  }

  return (
    <button
      onClick={() => setState('prompt')}
      className="btn btn--primary flex items-center gap-2 text-sm"
    >
      <Bell className="h-3.5 w-3.5" />
      {state === 'error' ? 'Try again' : 'Follow this area'}
    </button>
  );
}

// ─── Tab Content ──────────────────────────────────────────────────────────────

function InfoUnavailable({ message = 'Information not available.' }: { message?: string }) {
  return (
    <div className="rounded-lg border border-[var(--c-border)] bg-[var(--c-surface)] p-6 text-center">
      <FileText className="mx-auto mb-2 h-6 w-6 text-[var(--c-text-tertiary)]" />
      <p className="text-sm text-[var(--c-text-secondary)]">{message}</p>
    </div>
  );
}

function WeeklyReportPreviewCard({ report }: { report: CommunityWeeklyReportData | null | undefined }) {
  if (!report?.available || !report.stats) {
    return (
      <div className="rounded-lg border border-[var(--c-border)] bg-[var(--c-surface)] p-5">
        <div className="flex items-center gap-2">
          <FileText className="h-3.5 w-3.5 text-[var(--c-text-tertiary)]" />
          <span className="font-['Space_Grotesk'] text-base font-600 text-[var(--c-text-primary)]">
            Weekly report preview
          </span>
        </div>
        <p className="mt-3 text-sm text-[var(--c-text-secondary)]">Information not available.</p>
      </div>
    );
  }

  const topCategories = report.categories.slice(0, 3);

  return (
    <div className="rounded-lg border border-[var(--c-border)] bg-[var(--c-surface)] p-5">
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <FileText className="h-3.5 w-3.5 text-[var(--c-accent)]" />
        <span className="font-['Space_Grotesk'] text-base font-600 text-[var(--c-text-primary)]">
          Weekly report preview
        </span>
        <span className="ml-auto font-['DM_Mono'] text-[10px] text-[var(--c-text-tertiary)]">
          {report.dateRange.label}
        </span>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <div className="rounded-md border border-[var(--c-border)] bg-[var(--c-bg-secondary)] p-3">
          <p className="font-['DM_Mono'] text-[9px] uppercase tracking-[0.14em] text-[var(--c-text-tertiary)]">
            Applications
          </p>
          <p className="mt-1 font-['Space_Grotesk'] text-2xl font-700 text-[var(--c-text-primary)]">
            {report.stats.totalApplications}
          </p>
        </div>
        <div className="rounded-md border border-[var(--c-border)] bg-[var(--c-bg-secondary)] p-3">
          <p className="font-['DM_Mono'] text-[9px] uppercase tracking-[0.14em] text-[var(--c-text-tertiary)]">
            Scale 7+
          </p>
          <p className="mt-1 font-['Space_Grotesk'] text-2xl font-700 text-[var(--c-text-primary)]">
            {report.stats.highImpactApplications}
          </p>
        </div>
        <div className="rounded-md border border-[var(--c-border)] bg-[var(--c-bg-secondary)] p-3">
          <p className="font-['DM_Mono'] text-[9px] uppercase tracking-[0.14em] text-[var(--c-text-tertiary)]">
            Categories
          </p>
          <p className="mt-1 font-['Space_Grotesk'] text-2xl font-700 text-[var(--c-text-primary)]">
            {report.stats.categoryCount}
          </p>
        </div>
      </div>

      <p className="mt-4 text-sm text-[var(--c-text-secondary)]">{report.message}</p>
      {topCategories.length > 0 && (
        <div className="mt-4 space-y-2">
          {topCategories.map((category) => (
            <div key={category.name} className="flex items-center gap-3">
              <span className="min-w-0 flex-1 truncate text-sm text-[var(--c-text-secondary)]">
                {category.name}
              </span>
              <span className="font-['DM_Mono'] text-[10px] text-[var(--c-text-tertiary)]">
                {category.count} app{category.count !== 1 ? 's' : ''}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function OverviewTab({
  communitySlug,
  pageData,
  onOpenDrawer,
  onSelectRef,
}: {
  communitySlug: string;
  pageData: AreaPageData | null;
  onOpenDrawer: (app: Application) => void;
  onSelectRef: (ref: string) => void;
}) {
  const community = communityAreas.find((c) => c.slug === communitySlug) ?? communityAreas[0];
  if (!pageData) {
    return <InfoUnavailable message="Information not available for this community right now." />;
  }

  const stats = pageData.stats;
  const trends = pageData?.trends ?? null;
  const weeklyHistory = pageData?.weeklyHistory ?? null;
  const importantApps = pageData.importantApplications;
  const decisions = pageData.recentDecisions;
  const timeline = pageData.activityTimeline;
  const summary = pageData.weeklySummary;
  const mapPoints = pageData.mapPoints;

  return (
    <motion.div initial="hidden" animate="show" variants={{ show: { transition: { staggerChildren: 0.06 } } }} className="space-y-8">
      {/* Snapshot cards */}
      <motion.section variants={FADE_UP}>
        <div className="mb-3 flex items-center gap-2">
          <Activity className="h-3.5 w-3.5 text-[var(--c-accent)]" />
          <span className="font-['DM_Mono'] text-[10px] uppercase tracking-[0.14em] text-[var(--c-text-tertiary)]">
            At a glance
          </span>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          <SnapshotCard
            icon={FileText}
            label="Active"
            value={stats.activeApplications}
            trend={trends?.activeApplicationsTrend}
          />
          <SnapshotCard
            icon={TrendingUp}
            label="New this week"
            value={stats.newThisWeek}
            highlight
            trend={trends?.newThisWeekTrend}
            sub={trends ? `avg ${trends.weeklyAverage}/wk` : undefined}
            sparkData={weeklyHistory ?? undefined}
          />
          <SnapshotCard
            icon={Building2}
            label="Decisions this month"
            value={stats.decisionsThisMonth}
          />
          <SnapshotCard
            icon={Clock}
            label="Deadlines"
            value="N/A"
            sub="Info not available"
          />
          <SnapshotCard
            icon={AlertCircle}
            label="City-shaping"
            value={stats.highImpact}
            highlight={stats.highImpact > 0}
            trend={trends?.highImpactTrend}
          />
          <SnapshotCard
            icon={Home}
            label="Short-term lets"
            value={stats.shortTermLets}
            trend={trends?.stlTrend}
          />
        </div>
      </motion.section>

      {/* Weekly summary */}
      <motion.section variants={FADE_UP}>
        <div className="relative overflow-hidden rounded-lg border border-[var(--c-accent)]/25 bg-[var(--c-surface)] p-6">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_0%_0%,rgba(211,175,55,0.05),transparent)]" />
          <div className="absolute left-0 top-0 h-full w-0.5 bg-gradient-to-b from-[var(--c-accent)] to-transparent" />
          <div className="relative pl-4">
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <FileText className="h-3.5 w-3.5 text-[var(--c-accent)]" />
              <span className="font-['DM_Mono'] text-[10px] uppercase tracking-[0.14em] text-[var(--c-accent)]">
                This week in {community.name}
              </span>
              {summary && (
                <span className="ml-auto font-['DM_Mono'] text-[10px] text-[var(--c-text-tertiary)]">
                  {summary.week}
                  {summary.generatedFrom && (
                    <span className="ml-1.5 inline-flex items-center gap-1 rounded-full border border-emerald-500/20 bg-emerald-500/8 px-1.5 py-0.5 text-[9px] text-emerald-400">
                      From planning records
                    </span>
                  )}
                </span>
              )}
            </div>
            <h3 className="mb-2 font-['Space_Grotesk'] text-base font-600 text-[var(--c-text-primary)]">
              {summary.headline}
            </h3>
            <p className="mb-4 text-sm leading-relaxed text-[var(--c-text-secondary)]">
              {summary.body}
            </p>
            <div className="flex flex-wrap gap-2">
              {summary.keyFacts.map((fact) => (
                <span
                  key={fact}
                  className="rounded-full border border-[var(--c-accent)]/20 bg-[var(--c-accent)]/8 px-2.5 py-1 font-['DM_Mono'] text-[10px] text-[var(--c-accent)]"
                >
                  {fact}
                </span>
              ))}
            </div>
          </div>
        </div>
      </motion.section>

      {/* Weekly report digest */}
      <motion.section variants={FADE_UP}>
        <WeeklyReportPreviewCard report={pageData.weeklyReport} />
      </motion.section>

      {/* Important applications */}
      <motion.section variants={FADE_UP}>
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Star className="h-3.5 w-3.5 text-[var(--c-accent)]" />
            <span className="font-['Space_Grotesk'] text-base font-600 text-[var(--c-text-primary)]">
              Important applications
            </span>
          </div>
          <span className="font-['DM_Mono'] text-[10px] text-[var(--c-text-tertiary)]">
            Surfaced by CityScope
          </span>
        </div>
        {importantApps.length === 0 ? (
          <div className="rounded-lg border border-[var(--c-border)] bg-[var(--c-surface)] p-6 text-center">
            <FileText className="mx-auto mb-2 h-6 w-6 text-[var(--c-text-tertiary)]" />
            <p className="text-sm text-[var(--c-text-secondary)]">No notable applications at this time.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {importantApps.slice(0, 3).map((app) => (
              <ApplicationCard key={app.id} app={app} onOpenDrawer={onOpenDrawer} />
            ))}
          </div>
        )}
      </motion.section>

      {/* Two-column: deadline availability + recent decisions */}
      <motion.section variants={FADE_UP} className="grid gap-6 lg:grid-cols-2">
        {/* Deadline availability */}
        <div>
          <div className="mb-4 flex items-center gap-2">
            <Clock className="h-3.5 w-3.5 text-orange-400" />
            <span className="font-['Space_Grotesk'] text-base font-600 text-[var(--c-text-primary)]">
              Comment deadline data
            </span>
          </div>
          <div className="rounded-lg border border-[var(--c-border)] bg-[var(--c-surface)] p-5 text-sm text-[var(--c-text-secondary)]">
            Comment deadline information is not available.
          </div>
        </div>

        {/* Source-recorded outcomes may not include a decision date. */}
        <div>
          <div className="mb-4 flex items-center gap-2">
            <CheckCircle className="h-3.5 w-3.5 text-emerald-400" />
            <span className="font-['Space_Grotesk'] text-base font-600 text-[var(--c-text-primary)]">
              Decision records
            </span>
          </div>
          {decisions.length === 0 ? (
            <div className="rounded-lg border border-[var(--c-border)] bg-[var(--c-surface)] p-5 text-sm text-[var(--c-text-secondary)]">
              No recent decisions recorded.
            </div>
          ) : (
            <div className="rounded-lg border border-[var(--c-border)] bg-[var(--c-surface)] px-4 py-1">
              {decisions.map((dec) => (
                <DecisionRow key={dec.id} dec={dec} />
              ))}
            </div>
          )}
        </div>
      </motion.section>

      {/* Activity timeline */}
      <motion.section variants={FADE_UP}>
        <div className="mb-4 flex items-center gap-2">
          <Activity className="h-3.5 w-3.5 text-[var(--c-accent)]" />
          <span className="font-['Space_Grotesk'] text-base font-600 text-[var(--c-text-primary)]">
            Local activity
          </span>
          <span className="ml-1 h-1.5 w-1.5 animate-pulse rounded-full bg-[var(--c-accent)]" />
        </div>
        {timeline.length === 0 ? (
          <div className="rounded-lg border border-[var(--c-border)] bg-[var(--c-surface)] p-6 text-center text-sm text-[var(--c-text-secondary)]">
            Activity data updating — check back shortly.
          </div>
        ) : (
          <div className="rounded-lg border border-[var(--c-border)] bg-[var(--c-surface)] p-5">
            {timeline.map((event, i) => (
              <TimelineEventItem
                key={event.id}
                event={event}
                isLast={i === timeline.length - 1}
              />
            ))}
          </div>
        )}
      </motion.section>

      {/* Map */}
      <motion.section variants={FADE_UP}>
        <div className="mb-4 flex items-center gap-2">
          <MapPin className="h-3.5 w-3.5 text-[var(--c-text-tertiary)]" />
          <span className="font-['Space_Grotesk'] text-base font-600 text-[var(--c-text-primary)]">
            Application map
          </span>
          <span className="ml-auto font-['DM_Mono'] text-[10px] text-[var(--c-text-tertiary)]">
            {mapPoints.length > 0 ? `${mapPoints.length} pinned` : 'Georeferenced applications'}
          </span>
        </div>
        {mapPoints.length > 0 ? (
          <DeferredCommunityAreaMap
            mapPoints={mapPoints}
            onSelectRef={onSelectRef}
          />
        ) : (
          <MapPlaceholder />
        )}
      </motion.section>
    </motion.div>
  );
}

function ApplicationsTab({
  pageData,
  slug,
  onOpenDrawer,
}: {
  pageData: AreaPageData | null;
  slug: string;
  onOpenDrawer: (app: Application) => void;
}) {
  const [filter, setFilter] = useState<'all' | 'pending' | 'high' | 'stl'>('all');
  const apps = pageData?.applicationPreview?.length
    ? pageData.applicationPreview
    : pageData?.allApplications ?? [];

  const filtered = apps.filter((app) => {
    if (filter === 'pending') return app.status === 'pending';
    if (filter === 'high') return app.scaleRating === 'city';
    if (filter === 'stl') return app.tags.some((t) => t.toLowerCase().includes('short-term'));
    return true;
  });

  const filters: Array<{ key: typeof filter; label: string }> = [
    { key: 'all', label: 'All' },
    { key: 'pending', label: 'Pending' },
    { key: 'high', label: 'City-shaping' },
    { key: 'stl', label: 'Short-term lets' },
  ];

  return (
    <motion.div initial="hidden" animate="show" variants={FADE_IN} className="space-y-4">
      {!pageData && (
        <InfoUnavailable message="Information not available for this community right now." />
      )}
      {/* Filter pills */}
      <div className="flex flex-wrap gap-2">
        {filters.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setFilter(key)}
            className={`rounded-full border px-3 py-1 font-['DM_Mono'] text-[11px] uppercase tracking-wide transition-colors ${
              filter === key
                ? 'border-[var(--c-accent)] bg-[var(--c-accent)] text-black'
                : 'border-[var(--c-border)] text-[var(--c-text-secondary)] hover:border-[var(--c-accent)]/40 hover:text-[var(--c-text-primary)]'
            }`}
          >
            {label}
          </button>
        ))}
        <span className="ml-auto flex items-center gap-3 self-center">
          <span className="font-['DM_Mono'] text-[10px] text-[var(--c-text-tertiary)]">
            Showing {filtered.length}
            {pageData?.totalCount ? ` of ${pageData.totalCount}` : ''} application{filtered.length !== 1 ? 's' : ''}
          </span>
          <Link
            href={`/communities/${slug}/applications`}
            className="flex items-center gap-1 font-['DM_Mono'] text-[10px] text-[var(--c-accent)] hover:underline"
          >
            View all <ChevronRight className="h-3 w-3" />
          </Link>
        </span>
      </div>
      {filtered.length === 0 && (
        <div className="rounded-lg border border-[var(--c-border)] bg-[var(--c-surface)] p-8 text-center">
          <FileText className="mx-auto mb-2 h-6 w-6 text-[var(--c-text-tertiary)]" />
          <p className="text-sm text-[var(--c-text-secondary)]">No applications match this filter.</p>
        </div>
      )}
      {filtered.map((app) => (
        <ApplicationCard key={app.id} app={app} onOpenDrawer={onOpenDrawer} />
      ))}
    </motion.div>
  );
}

function DeadlinesTab({ pageData }: { pageData: AreaPageData | null }) {
  return (
    <motion.div initial="hidden" animate="show" variants={FADE_IN} className="space-y-6">
      <div>
        <div className="mb-3 flex items-center gap-2">
          <Clock className="h-3.5 w-3.5 text-orange-400" />
          <span className="font-['Space_Grotesk'] text-base font-600 text-[var(--c-text-primary)]">
            Open for comment
          </span>
        </div>
        <div className="rounded-lg border border-[var(--c-border)] bg-[var(--c-surface)] p-8 text-center">
          <Clock className="mx-auto mb-2 h-6 w-6 text-[var(--c-text-tertiary)]" />
          <p className="text-sm text-[var(--c-text-secondary)]">Comment deadline information is not available.</p>
        </div>
      </div>
      <div className="rounded-lg border border-[var(--c-border)] bg-[var(--c-surface)] p-6 text-center">
        <Clock className="mx-auto mb-2 h-6 w-6 text-[var(--c-text-tertiary)]" />
        <p className="font-['Space_Grotesk'] text-sm font-600 text-[var(--c-text-primary)]">
          Deadline data not available
        </p>
        <p className="mt-1 text-xs text-[var(--c-text-secondary)]">
          CityScope will show comment deadlines when the planning API provides reliable deadline fields.
        </p>
      </div>
    </motion.div>
  );
}

function ProjectsTab({ pageData }: { pageData: AreaPageData | null }) {
  const projects = pageData?.majorProjects ?? [];
  return (
    <motion.div initial="hidden" animate="show" variants={FADE_IN} className="space-y-4">
      <div className="flex items-center gap-2">
        <Layers className="h-3.5 w-3.5 text-[var(--c-accent)]" />
        <span className="font-['Space_Grotesk'] text-base font-600 text-[var(--c-text-primary)]">
          Major local projects
        </span>
        <span className="ml-1 rounded-full border border-[var(--c-border)] bg-[var(--c-surface)] px-2 py-0.5 font-['DM_Mono'] text-[10px] text-[var(--c-text-tertiary)]">
          Grouped by CityScope
        </span>
      </div>
      <p className="text-sm text-[var(--c-text-secondary)]">
        CityScope automatically detects clusters of related applications — by address, applicant, or
        site — and groups them into projects so you can track complex developments as a whole.
      </p>
      {projects.length === 0 ? (
        <div className="rounded-lg border border-[var(--c-border)] bg-[var(--c-surface)] p-8 text-center">
          <Layers className="mx-auto mb-2 h-6 w-6 text-[var(--c-text-tertiary)]" />
          <p className="text-sm text-[var(--c-text-secondary)]">
            No project clusters detected yet. CityScope looks for applications sharing a site, address, or group.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {projects.map((project) => (
            <ProjectCard key={project.id} project={project} />
          ))}
        </div>
      )}
    </motion.div>
  );
}

interface LiveDiscussionSummary {
  reference_no: string;
  comment_count: number;
  thread_count: number;
  preview_body?: string;
  address?: string;
}

function DiscussionTab({ pageData }: { pageData: AreaPageData | null }) {
  const [liveSummaries, setLiveSummaries] = useState<LiveDiscussionSummary[] | null>(null);
  const [loading, setLoading] = useState(false);

  // Fetch real discussion summaries for the top applications in this area
  useEffect(() => {
    const discussionApps = pageData?.applicationPreview?.length
      ? pageData.applicationPreview
      : pageData?.allApplications ?? [];
    const refs = discussionApps.slice(0, 80).map((a) => a.reference).filter(Boolean);
    if (!refs?.length) return;

    setLoading(true);

    const url = `${CITY_SCOPE_API_ORIGIN}/api/v1/discussions/summaries?references=${encodeURIComponent(refs.join(','))}`;
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 5_000);

    fetch(url, { headers: { Accept: 'application/json' }, signal: controller.signal })
      .then((r) => r.json())
      .then((data) => {
        const raw: Array<{ reference_no: string; comment_count: number; thread_count: number; preview_comments?: Array<{ body: string }> }> =
          data?.summaries ?? (Array.isArray(data) ? data : []);

        const mapped: LiveDiscussionSummary[] = raw
          .filter((s) => (s.comment_count ?? 0) > 0)
          .map((s) => {
            const app = discussionApps.find((a) => a.reference === s.reference_no);
            return {
              reference_no: s.reference_no,
              comment_count: s.comment_count,
              thread_count: s.thread_count,
              preview_body: s.preview_comments?.[0]?.body,
              address: app?.address,
            };
          });

        setLiveSummaries(mapped);
      })
      .catch(() => setLiveSummaries([]))
      .finally(() => {
        window.clearTimeout(timeoutId);
        setLoading(false);
      });

    return () => {
      window.clearTimeout(timeoutId);
      controller.abort();
    };
  }, [pageData]);

  const hasLiveDiscussions = liveSummaries && liveSummaries.length > 0;
  const communityName = pageData?.communityName ?? 'this area';

  return (
    <motion.div initial="hidden" animate="show" variants={FADE_IN} className="space-y-6">
      {/* Discussion header */}
      <div>
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageCircle className="h-3.5 w-3.5 text-[var(--c-text-tertiary)]" />
            <span className="font-['Space_Grotesk'] text-base font-600 text-[var(--c-text-primary)]">
              Questions and discussion
            </span>
            {hasLiveDiscussions && (
              <span className="rounded-full border border-[var(--c-border)] bg-[var(--c-surface)] px-2 py-0.5 font-['DM_Mono'] text-[10px] text-[var(--c-text-secondary)]">
                {liveSummaries.reduce((n, s) => n + s.comment_count, 0)} comments
              </span>
            )}
          </div>
        </div>

        <div className="mb-3 rounded-lg border border-[var(--c-border)] bg-[var(--c-surface)] p-4 text-sm text-[var(--c-text-secondary)]">
          Discussion is secondary to the planning data. This board updates automatically as new
          applications and decisions are published, even with no comments.
        </div>

        {/* Live discussion summaries */}
        {loading && (
          <div className="space-y-2">
            {[1, 2].map((n) => (
              <div key={n} className="h-16 animate-pulse rounded-lg border border-[var(--c-border)] bg-[var(--c-surface)]" />
            ))}
          </div>
        )}

        {!loading && hasLiveDiscussions && (
          <div className="space-y-3">
            {liveSummaries.map((s) => (
              <Link
                key={s.reference_no}
                href={buildPlanningApplicationPath(s.reference_no)}
                className="block rounded-lg border border-[var(--c-border)] bg-[var(--c-surface)] p-4 transition-colors hover:border-[var(--c-accent)]/30"
              >
                <div className="mb-1 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate font-['DM_Mono'] text-[10px] text-[var(--c-text-tertiary)]">
                      {s.address?.split(',')[0] ?? s.reference_no}
                    </p>
                    {s.preview_body && (
                      <p className="mt-1 text-sm text-[var(--c-text-secondary)] line-clamp-2">
                        &ldquo;{s.preview_body.slice(0, 160)}&rdquo;
                      </p>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center gap-1 rounded-full border border-[var(--c-border)] bg-[var(--c-surface-2,var(--c-surface))] px-2 py-0.5 font-['DM_Mono'] text-[10px] text-[var(--c-text-secondary)]">
                    <MessageCircle className="h-2.5 w-2.5" />
                    {s.comment_count}
                  </div>
                </div>
                <p className="font-['DM_Mono'] text-[9px] text-[var(--c-text-tertiary)]">
                  {s.reference_no} · {s.thread_count} thread{s.thread_count !== 1 ? 's' : ''}
                </p>
              </Link>
            ))}
          </div>
        )}

        {!loading && liveSummaries !== null && !hasLiveDiscussions && (
          <div className="rounded-lg border border-[var(--c-border)] bg-[var(--c-surface)] p-8 text-center">
            <MessageCircle className="mx-auto mb-3 h-7 w-7 text-[var(--c-text-tertiary)]" />
            <p className="font-['Space_Grotesk'] text-sm font-600 text-[var(--c-text-primary)]">
              Discussion is quiet in {communityName}
            </p>
            <p className="mt-1 text-xs text-[var(--c-text-secondary)]">
              The board still updates automatically as new applications and decisions are published, even with no comments.
            </p>
          </div>
        )}

        {!loading && liveSummaries === null && (
          <InfoUnavailable message="Discussion information is not available." />
        )}
      </div>
    </motion.div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

const TABS: Array<{ id: Tab; label: string; icon: LucideIcon }> = [
  { id: 'overview', label: 'Overview', icon: Activity },
  { id: 'applications', label: 'Applications', icon: FileText },
  { id: 'deadlines', label: 'Deadlines', icon: Clock },
  { id: 'projects', label: 'Projects', icon: Layers },
  { id: 'discussion', label: 'Discussion', icon: MessageCircle },
];

interface SlugPageProps {
  pageData: AreaPageData | null;
  slug: string;
  retryAt?: number;
}

const COMMUNITY_REFRESH_INTERVAL_MS = 30 * 60 * 1000;

export default function CommunityBoardPage({ pageData: initialPageData, slug: slugProp, retryAt: initialRetryAt = 0 }: SlugPageProps) {
  const router = useRouter();
  const { slug } = router.query;
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [liveData, setLiveData] = useState<AreaPageData | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [retryAt, setRetryAt] = useState(initialRetryAt);
  const retryAtRef = useRef(initialRetryAt);
  const refreshInFlight = useRef(false);
  const [lastRefreshed, setLastRefreshed] = useState<string | null>(null);
  const [selectedApp, setSelectedApp] = useState<Application | null>(null);
  const [hasHydrated, setHasHydrated] = useState(false);

  const pageData = liveData ?? initialPageData;
  const slugStr = Array.isArray(slug) ? slug[0] : slug ?? slugProp ?? 'leith';
  const community = communityAreas.find((c) => c.slug === slugStr) ?? communityAreas[0];

  useEffect(() => {
    setHasHydrated(true);
  }, []);

  const fetchFreshData = useCallback(async (currentSlug: string) => {
    if (refreshInFlight.current || Date.now() < retryAtRef.current) return;
    refreshInFlight.current = true;
    setRefreshing(true);
    try {
      const res = await fetch(`/api/communities/${encodeURIComponent(currentSlug)}?variant=preview`);
      const waitMs = parseRetryAfterMs(res.headers.get('Retry-After'));
      if (res.status === 429 || waitMs !== undefined) {
        retryAtRef.current = Date.now() + Math.max(1000, waitMs ?? 60_000);
        setRetryAt(retryAtRef.current);
      }
      if (res.ok) {
        const fresh: AreaPageData = await res.json();
        // A bounded API refresh may return a soft partial response while the
        // ISR-rendered page remains healthy. Keep the visible page intact.
        if (Array.isArray(fresh?.applicationPreview) && fresh?.fetchedAt) {
          setLiveData(fresh);
          setLastRefreshed(fresh.fetchedAt);
        }
      }
    } catch {
      // silent — ISR data is still shown
    } finally {
      refreshInFlight.current = false;
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    if (!retryAt) return;
    const timer = setTimeout(() => setRetryAt(0), Math.max(0, retryAt - Date.now()));
    return () => clearTimeout(timer);
  }, [retryAt]);

  // All applications across tabs for drawer lookup
  const allAppsForDrawer = useMemo(() => {
    const apps = [
      ...(pageData?.applicationPreview ?? []),
      ...(pageData?.allApplications ?? []),
      ...(pageData?.importantApplications ?? []),
    ];
    const seen = new Set<string>();
    return apps.filter((a) => {
      if (seen.has(a.reference)) return false;
      seen.add(a.reference);
      return true;
    });
  }, [pageData]);

  const openDrawerForRef = useCallback(
    (ref: string) => {
      const app = allAppsForDrawer.find((a) => a.reference === ref);
      if (app) setSelectedApp(app);
    },
    [allAppsForDrawer]
  );

  // ── "New since last visit" tracking ──────────────────────────────────────
  const [newSinceLastVisit, setNewSinceLastVisit] = useState<number | null>(null);
  const [visitBannerDismissed, setVisitBannerDismissed] = useState(false);

  useEffect(() => {
    const visitApps = pageData?.applicationPreview?.length
      ? pageData.applicationPreview
      : pageData?.allApplications ?? [];
    if (!slugStr || !visitApps.length) return;
    const key = `community_last_visit_${slugStr}`;
    const stored = typeof window !== 'undefined' ? localStorage.getItem(key) : null;
    if (stored) {
      const lastMs = parseInt(stored, 10);
      const uniqueRefs = new Set(
        visitApps
          .filter((a) => a.receivedDate && new Date(a.receivedDate).getTime() > lastMs)
          .map((a) => a.reference)
      );

      if (uniqueRefs.size > 0) {
        setNewSinceLastVisit(uniqueRefs.size);
      }
    }
    // Update last visit timestamp
    if (typeof window !== 'undefined') {
      localStorage.setItem(key, String(Date.now()));
    }
  }, [slugStr, pageData]); // eslint-disable-line react-hooks/exhaustive-deps

  // The ISR page is the primary load path. Avoid firing a second expensive
  // community API request during first paint; keep long-lived tabs fresh.
  useEffect(() => {
    if (!slugStr) return;
    if (!initialPageData) {
      const timer = setTimeout(() => fetchFreshData(slugStr), Math.max(0, initialRetryAt - Date.now()));
      return () => clearTimeout(timer);
    }
    const interval = setInterval(() => {
      if (typeof document === 'undefined' || document.visibilityState === 'visible') {
        fetchFreshData(slugStr);
      }
    }, COMMUNITY_REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [slugStr, initialPageData, initialRetryAt, fetchFreshData]);

  return (
    <>
      <Meta
        title={`${community.name} Planning Board — CityScope Communities`}
        description={
          pageData?.stats
            ? `${community.name} planning board — ${pageData.stats.activeApplications} active applications, ${pageData.stats.newThisWeek} dated this week. Planning records and source links from CityScope.`
            : `Track planning applications, decisions, short-term lets and development activity across ${community.name}. Local planning intelligence from CityScope.`
        }
        keywords={`${community.name} planning, Edinburgh ${community.name} planning applications, ${community.postcodes.join(' ')} planning`}
      />

      <div className="min-h-screen bg-[var(--c-bg)]">
        <div className="pointer-events-none fixed inset-0 neo-civic-grid opacity-30" />

        {/* ── Area header ────────────────────────────────────────────────── */}
        <div className="relative border-b border-[var(--c-border)] bg-[var(--c-surface)] pt-20 pb-0">
          {/* Glow */}
          <div className="pointer-events-none absolute inset-x-0 top-0 h-48 bg-[radial-gradient(ellipse_70%_100%_at_50%_0%,rgba(211,175,55,0.07),transparent)]" />

          <div className="relative mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
            {/* Area label */}
            <div className="mb-3 flex items-center gap-2">
              <span className="rounded-full border border-[var(--c-accent)]/25 bg-[var(--c-accent)]/8 px-3 py-1 font-['DM_Mono'] text-[10px] uppercase tracking-[0.14em] text-[var(--c-accent)]">
                Edinburgh · {community.postcodes.join(' / ')}
              </span>
              <span
                className={`rounded-full border px-2 py-0.5 font-['DM_Mono'] text-[10px] uppercase tracking-wide ${statusColorMap.gray}`}
              >
                <span className="mr-1 inline-block h-1.5 w-1.5 rounded-full bg-current" />
                {pageData ? 'Planning records' : 'Data unavailable'}
              </span>
            </div>

            {/* Title */}
            <h1 className="heading-2 mb-2 text-[var(--c-text-primary)]">
              {community.name} Planning Board
            </h1>
            <p className="mb-6 max-w-2xl text-sm leading-relaxed text-[var(--c-text-secondary)]">
              Track planning applications, decisions, short-term lets and development activity across{' '}
              {community.name}. CityScope summarises new applications, highlights major proposals,
              and groups related applications into local projects.
            </p>

            {/* "New since last visit" banner */}
            {newSinceLastVisit && newSinceLastVisit > 0 && !visitBannerDismissed && (
              <motion.div
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="mb-4 flex items-center justify-between gap-3 rounded-lg border border-emerald-500/30 bg-emerald-500/8 px-4 py-2.5"
              >
                <div className="flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                  <span className="font-['DM_Mono'] text-[11px] text-emerald-400">
                    {newSinceLastVisit} new application{newSinceLastVisit !== 1 ? 's' : ''} since your last visit
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setActiveTab('applications')}
                    className="font-['DM_Mono'] text-[10px] text-emerald-400 underline hover:no-underline"
                  >
                    View
                  </button>
                  <button
                    onClick={() => setVisitBannerDismissed(true)}
                    className="text-emerald-400/60 hover:text-emerald-400"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              </motion.div>
            )}

            {/* Action buttons */}
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <FollowAreaButton slug={slugStr} areaName={community.name} />
              <button className="btn flex items-center gap-2 text-sm" onClick={() => setActiveTab('overview')}>
                <Map className="h-3.5 w-3.5" /> View map
              </button>
            </div>

            {/* Freshness indicator */}
            <div className="mb-4 flex flex-wrap items-center gap-3">
              {hasHydrated && (lastRefreshed || pageData?.fetchedAt) && (
                <span className="font-['DM_Mono'] text-[10px] text-[var(--c-text-tertiary)]">
                  Board refreshed {formatDateTime(lastRefreshed ?? pageData!.fetchedAt)}
                </span>
              )}
              <button
                onClick={() => fetchFreshData(slugStr)}
                disabled={refreshing || retryAt > Date.now()}
                className="flex items-center gap-1 rounded-full border border-[var(--c-border)] bg-[var(--c-surface)] px-2.5 py-0.5 font-['DM_Mono'] text-[10px] text-[var(--c-text-secondary)] transition-colors hover:border-[var(--c-accent)]/40 hover:text-[var(--c-accent)] disabled:opacity-50"
              >
                <span
                  className={`h-1.5 w-1.5 rounded-full ${
                    refreshing
                      ? 'animate-pulse bg-[var(--c-accent)]'
                      : liveData
                      ? 'bg-emerald-400'
                      : 'bg-[var(--c-text-tertiary)]'
                  }`}
                />
                {refreshing ? 'Refreshing…' : retryAt > Date.now() ? 'Try again shortly' : 'Refresh'}
              </button>
            </div>

            {/* Tabs */}
            <div className="flex gap-0 overflow-x-auto scrollbar-hide -mb-px">
              {TABS.map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => setActiveTab(id)}
                  className={`relative flex shrink-0 items-center gap-2 border-b-2 px-4 py-3 font-['DM_Mono'] text-[11px] uppercase tracking-wider transition-colors ${
                    activeTab === id
                      ? 'border-[var(--c-accent)] text-[var(--c-accent)]'
                      : 'border-transparent text-[var(--c-text-tertiary)] hover:text-[var(--c-text-secondary)]'
                  }`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* ── Tab content ────────────────────────────────────────────────── */}
        <div className="relative mx-auto max-w-5xl px-4 py-8 sm:px-6 lg:px-8">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25 }}
            >
              {activeTab === 'overview' && (
                <OverviewTab
                  communitySlug={slugStr}
                  pageData={pageData}
                  onOpenDrawer={setSelectedApp}
                  onSelectRef={openDrawerForRef}
                />
              )}
              {activeTab === 'applications' && (
                <ApplicationsTab
                  pageData={pageData}
                  slug={slugStr}
                  onOpenDrawer={setSelectedApp}
                />
              )}
              {activeTab === 'deadlines' && <DeadlinesTab pageData={pageData} />}
              {activeTab === 'projects' && <ProjectsTab pageData={pageData} />}
              {activeTab === 'discussion' && <DiscussionTab pageData={pageData} />}
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="h-16" />
      </div>

      {/* Application detail drawer */}
      <ApplicationDetailDrawer
        app={selectedApp}
        onClose={() => setSelectedApp(null)}
      />
    </>
  );
}

// ─── Data fetching (ISR) ──────────────────────────────────────────────────────

export async function getStaticPaths() {
  return {
    // Do not fan out every community read-model query during a Vercel build.
    // Each area is still generated as crawlable HTML on first request and
    // cached by ISR, but one deploy cannot stampede the shared API/database.
    paths: [],
    fallback: 'blocking',
  };
}

export async function getStaticProps({ params }: { params: { slug: string } }) {
  const slug = params?.slug ?? 'leith';

  try {
    const { buildAreaPageData } = await import('../../lib/communities/dataService');
    const pageData = await buildAreaPageData(slug, { includeAllApplications: false });

    if (!pageData) {
      return { notFound: true };
    }

    return {
      props: { pageData, slug },
      revalidate: 600, // 10 minutes
    };
  } catch (err) {
    console.error(`[Community board] getStaticProps failed for slug "${slug}":`, err);
    return {
      props: { pageData: null, slug, retryAt: rateLimitRetryAt(err) ?? 0 },
      revalidate: 120,
    };
  }
}
